// Product page island: swatch/button picker over the embedded variant matrix, then a fresh
// price/stock check through `get_variant`. Values that don't exist with the other choices, or
// are sold out, are struck through; choosing one switches the other options to a variant that
// exists, so a missing combination can never be added. Without JavaScript the options are
// plain radio buttons and the server resolves the variant.

const API = "/api/method/softech_commerce.api.catalog.get_variant";

export function initProduct(root) {
	const data = JSON.parse(root.dataset.variants || "{}");
	const form = root.querySelector("[data-add-to-cart]");
	if (!form || !data.variants || !data.variants.length) return;

	const groups = [...form.querySelectorAll("fieldset[data-attribute]")];
	const names = groups.map((g) => g.dataset.attribute);
	const price = root.querySelector("[data-product-price]");
	const stock = root.querySelector("[data-product-stock]");
	const sku = root.querySelector("[data-product-sku]");
	const image = root.querySelector("[data-product-image]");
	const code = form.querySelector("[data-item-code]");
	const button = form.querySelector("[data-add-button]");
	const hint = form.querySelector("[data-variant-hint]");
	const template = root.dataset.template;
	let request = 0;

	// what the server rendered for the product as a whole, restored without a full choice
	// (the price/stock block is optional: the owner may leave it out of the product page)
	const initialPrice = price ? price.textContent : "";
	const initialImage = image ? image.getAttribute("src") : null;

	const chosen = () =>
		Object.fromEntries(
			groups.map((g) => {
				const input = g.querySelector("input:checked");
				return [g.dataset.attribute, input ? input.value : ""];
			})
		);
	// v fits every chosen value, except the attribute `skip`
	const fits = (v, choice, skip) =>
		names.every((n) => n === skip || !choice[n] || v.attributes[n] === choice[n]);
	const buyable = (v) => v.badge && v.badge.can_buy;

	const mark = (choice) => {
		groups.forEach((group) => {
			const name = group.dataset.attribute;
			const chosenLabel = group.querySelector("[data-chosen]");
			if (chosenLabel) chosenLabel.textContent = choice[name] || "";
			group.querySelectorAll("input").forEach((input) => {
				const matching = data.variants.filter(
					(v) => v.attributes[name] === input.value && fits(v, choice, name)
				);
				const missing = !matching.length;
				const soldOut = !missing && !matching.some(buyable);
				const option = input.closest(".variant-option");
				option.classList.toggle("is-missing", missing);
				option.classList.toggle("is-unavailable", soldOut);
				const state = option.querySelector("[data-option-state]");
				if (state)
					state.textContent = missing
						? ` (${group.dataset.missing})`
						: soldOut
						? ` (${group.dataset.soldOut})`
						: "";
			});
		});
	};

	const show = (variant, message) => {
		code.value = variant ? variant.item_code : "";
		button.disabled = !(variant && buyable(variant));
		if (price)
			price.textContent =
				variant && variant.price_display ? variant.price_display : initialPrice;
		if (stock) stock.textContent = variant && variant.badge ? variant.badge.label : "";
		if (sku) {
			sku.textContent = variant ? variant.item_code : "";
			sku.parentElement.hidden = !variant;
		}
		hint.textContent = message || "";
		if (image) image.src = (variant && variant.image) || initialImage;
	};

	const remember = (variant) => {
		const url = new URL(window.location.href);
		if (variant) url.searchParams.set("variant", variant.item_code);
		else url.searchParams.delete("variant");
		history.replaceState(history.state, "", url);
	};

	const update = async () => {
		const choice = chosen();
		mark(choice);
		if (Object.values(choice).some((v) => !v)) {
			show(null, hint.dataset.choose);
			return remember(null);
		}
		const local = data.variants.find((v) => fits(v, choice));
		if (!local) return show(null, hint.dataset.unavailable);
		show(local);
		remember(local);

		const id = ++request;
		const query = new URLSearchParams({ template, attributes: JSON.stringify(choice) });
		try {
			const response = await fetch(`${API}?${query}`, {
				headers: { Accept: "application/json" },
			});
			if (!response.ok || id !== request) return;
			const fresh = (await response.json()).message;
			if (fresh && fresh.item_code === local.item_code) show({ ...local, ...fresh });
		} catch (e) {
			// keep the server-rendered data when offline
		}
	};

	// a value that doesn't exist with the other choices: move the others to a variant that has it
	form.addEventListener("change", (event) => {
		const group = event.target.closest("fieldset[data-attribute]");
		if (!group) return;
		const name = group.dataset.attribute;
		const choice = chosen();
		if (!data.variants.some((v) => fits(v, choice))) {
			const withValue = data.variants.filter((v) => v.attributes[name] === choice[name]);
			const best =
				withValue.find((v) => buyable(v) && fits(v, choice, name)) ||
				withValue.find(buyable) ||
				withValue[0];
			if (best) {
				groups.forEach((g) => {
					const value = best.attributes[g.dataset.attribute];
					g.querySelectorAll("input").forEach((input) => {
						input.checked = input.value === value;
					});
				});
			}
		}
		update();
	});
	update();
}
